import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ICostCentreState } from "../../utils/costCentre.types";
import { ApiStatus } from "../../utils/api.types";
import {
    fetchCostCentres,
    fetchCostCentreById,
    createCostCentre,
    updateCostCentre,
    deleteCostCentre
} from "./costCentreThunk";

/**
 * Cost Centre slice initial state
 */
const initialState: ICostCentreState = {
    costCentres: [],
    activeCostCentres: [],
    selectedCostCentre: null,
    status: ApiStatus.IDLE,
    error: null
};

/**
 * Cost Centre Slice
 */
const costCentreSlice = createSlice({
    name: 'costCentre',
    initialState,
    reducers: {
        clearSelectedCostCentre: (state) => {
            state.selectedCostCentre = null;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all cost centres
            .addCase(fetchCostCentres.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchCostCentres.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const data = Array.isArray(action.payload.data) ? action.payload.data : [];
                state.costCentres = data;
                state.activeCostCentres = data.filter(cc => cc.isActive);
                state.error = null;
            })
            .addCase(fetchCostCentres.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to fetch cost centres';
                toast.error(state.error);
            })

            // Fetch cost centre by ID
            .addCase(fetchCostCentreById.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchCostCentreById.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.selectedCostCentre = action.payload.data as any;
                state.error = null;
            })
            .addCase(fetchCostCentreById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to fetch cost centre';
                toast.error(state.error);
            })

            // Create cost centre
            .addCase(createCostCentre.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(createCostCentre.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                if (action.payload.data && !Array.isArray(action.payload.data)) {
                    state.costCentres.push(action.payload.data);
                    if (action.payload.data.isActive) {
                        state.activeCostCentres.push(action.payload.data);
                    }
                }
                state.error = null;
                toast.success(action.payload.message || 'Cost Centre created successfully');
            })
            .addCase(createCostCentre.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to create cost centre';
                toast.error(state.error);
            })

            // Update cost centre
            .addCase(updateCostCentre.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateCostCentre.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                if (action.payload.data && !Array.isArray(action.payload.data)) {
                    const data = action.payload.data;
                    const index = state.costCentres.findIndex(cc => cc._id === data._id);
                    if (index !== -1) {
                        state.costCentres[index] = data;
                    }
                    // Update active list
                    state.activeCostCentres = state.costCentres.filter(cc => cc.isActive);
                }
                state.error = null;
                toast.success(action.payload.message || 'Cost Centre updated successfully');
            })
            .addCase(updateCostCentre.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to update cost centre';
                toast.error(state.error);
            })

            // Delete cost centre
            .addCase(deleteCostCentre.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteCostCentre.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                if (action.payload.data && !Array.isArray(action.payload.data)) {
                    const data = action.payload.data;
                    state.costCentres = state.costCentres.filter(cc => cc._id !== data._id);
                    state.activeCostCentres = state.activeCostCentres.filter(cc => cc._id !== data._id);
                }
                state.error = null;
                toast.success(action.payload.message || 'Cost Centre deleted successfully');
            })
            .addCase(deleteCostCentre.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to delete cost centre';
                toast.error(state.error);
            });
    }
});

export const { clearSelectedCostCentre, clearError } = costCentreSlice.actions;
export default costCentreSlice.reducer;
